import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const cls = "w-full bg-[color:var(--ms-warm-white)] border border-[color:var(--ms-border)] focus:border-[color:var(--ms-charcoal)] px-3 py-2.5 text-sm outline-none text-[color:var(--ms-charcoal)]";

export default function AdminDiscounts() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/admin/discounts").then(r => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            const payload = { ...editing, value: parseFloat(editing.value), min_subtotal: parseFloat(editing.min_subtotal || 0) };
            if (editing.id) await api.put(`/admin/discounts/${editing.id}`, payload);
            else await api.post("/admin/discounts", payload);
            toast.success("Salvato");
            setEditing(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Salvataggio fallito"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare codice?")) return;
        await api.delete(`/admin/discounts/${id}`);
        toast.success("Eliminato");
        load();
    };

    const startNew = () => setEditing({ code: "", discount_type: "percent", value: 10, min_subtotal: 0, active: true });

    return (
        <div data-testid="admin-discounts">
            <div className="flex justify-between items-center mb-10">
                <div>
                    <div className="overline mb-2">Marketing</div>
                    <h1 className="font-display text-4xl text-[color:var(--ms-charcoal)]">Codici Sconto</h1>
                </div>
                <button onClick={startNew} className="bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] px-5 py-3 text-xs tracking-[0.2em] uppercase flex items-center gap-2 hover:bg-[color:var(--ms-sage-dark)]" data-testid="discount-new"><Plus size={14} /> Nuovo codice</button>
            </div>

            {items.length === 0 ? (
                <div className="panel p-16 text-center text-[color:var(--ms-muted)]">Nessun codice sconto ancora.</div>
            ) : (
                <div className="panel">
                    <table className="w-full text-sm">
                        <thead className="text-left text-[color:var(--ms-muted)] text-xs uppercase tracking-[0.2em]">
                            <tr>
                                <th className="px-4 py-3 border-b border-[color:var(--ms-border)]">Codice</th>
                                <th className="px-4 py-3 border-b border-[color:var(--ms-border)]">Tipo</th>
                                <th className="px-4 py-3 border-b border-[color:var(--ms-border)]">Valore</th>
                                <th className="px-4 py-3 border-b border-[color:var(--ms-border)]">Sub. minimo</th>
                                <th className="px-4 py-3 border-b border-[color:var(--ms-border)]">Attivo</th>
                                <th className="px-4 py-3 border-b border-[color:var(--ms-border)]"></th>
                            </tr>
                        </thead>
                        <tbody className="text-[color:var(--ms-charcoal)]">
                            {items.map(d => (
                                <tr key={d.id} className="border-b border-[color:var(--ms-border)] cursor-pointer hover:bg-[color:var(--ms-cream)]" onClick={() => setEditing(d)} data-testid={`discount-row-${d.id}`}>
                                    <td className="px-4 py-3 font-mono text-[color:var(--ms-sage-dark)]">{d.code}</td>
                                    <td className="px-4 py-3">{d.discount_type === "percent" ? "Percentuale" : "Fisso"}</td>
                                    <td className="px-4 py-3">{d.discount_type === "percent" ? `${d.value}%` : `€${d.value}`}</td>
                                    <td className="px-4 py-3">€{d.min_subtotal}</td>
                                    <td className="px-4 py-3"><span className={`overline ${d.active ? "text-[color:var(--ms-sage-dark)]" : "text-[color:var(--ms-muted)]"}`}>{d.active ? "Attivo" : "Non attivo"}</span></td>
                                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}><button onClick={() => remove(d.id)} className="hover:text-[color:var(--ms-terracotta)]"><Trash2 size={14} /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[color:var(--ms-warm-white)] border border-[color:var(--ms-border)] max-w-md w-full p-8 space-y-4" data-testid="discount-editor">
                        <h2 className="font-display text-2xl text-[color:var(--ms-charcoal)]">{editing.id ? "Modifica" : "Nuovo codice"}</h2>
                        <div><label className="overline block mb-2">Codice</label><input value={editing.code} onChange={e => setEditing({ ...editing, code: e.target.value.toUpperCase() })} className={cls} data-testid="discount-code" /></div>
                        <div><label className="overline block mb-2">Tipo</label>
                            <select value={editing.discount_type} onChange={e => setEditing({ ...editing, discount_type: e.target.value })} className={cls}>
                                <option value="percent">Percentuale (%)</option>
                                <option value="fixed">Fisso (€)</option>
                            </select>
                        </div>
                        <div><label className="overline block mb-2">Valore</label><input type="number" step="0.01" value={editing.value} onChange={e => setEditing({ ...editing, value: e.target.value })} className={cls} data-testid="discount-value" /></div>
                        <div><label className="overline block mb-2">Subtotale minimo (€)</label><input type="number" step="0.01" value={editing.min_subtotal} onChange={e => setEditing({ ...editing, min_subtotal: e.target.value })} className={cls} /></div>
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} className="accent-[color:var(--ms-sage-dark)]" /> Attivo</label>
                        <div className="flex gap-3 justify-end pt-3">
                            <button onClick={() => setEditing(null)} className="border border-[color:var(--ms-border)] hover:border-[color:var(--ms-charcoal)] px-4 py-2 text-xs tracking-[0.2em] uppercase">Annulla</button>
                            <button onClick={save} className="bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] px-5 py-2 text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--ms-sage-dark)]" data-testid="discount-save">Salva</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
